/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useContext,
  useLayoutEffect,
  useRef,
} from 'react';
import { CanvasContext } from './CanvasContext';
import { StickersLayer } from 'components/tools/StickersTool';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function Canvas({ image }) {
  const {
    canvasRef,
    overlayRef,
    imageRef,
    activeTool,
    transformMode,
    rotatePreviewDeg,
    setCanvasScale,
    canvasRectRef,
    scaleRef,
    canvasFilter,
    setTextInsertPoint,
    selectedStickerSrc,
    addSticker,
    redrawTrigger,
  } = useContext(CanvasContext);

  const [cursor, setCursor] = useState('auto');
  const [showStickerPrompt, setShowStickerPrompt] = useState(false);
  const wrapperRef = useRef(null);

  // store the visible canvas box so we can draw a “clipping frame” during rotate preview
  const rotateFrameRef = useRef({ left: 0, top: 0, width: 0, height: 0 });

  function displayImageFromObjectURL(objectURL) {
    const img = new Image();
    img.onload = function () {
      imageRef.current = img;

      const canvas = canvasRef.current;
      if (!canvas) return;

      // Clear any leftover inline sizing
      canvas.style.width = '';
      canvas.style.height = '';
      canvas.style.transform = '';

      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      canvas.width = img.width;
      canvas.height = img.height;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      updateMetricsAndOverlay();
    };
    img.src = objectURL;
  }

  const updateMetricsAndOverlay = () => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const canvasRect = canvas.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    canvasRectRef.current = canvasRect;
    scaleRef.current = {
      x: canvas.width / canvasRect.width,
      y: canvas.height / canvasRect.height,
    };

    setCanvasScale(canvasRect.width / canvas.width);

    // ✅ Only used for the rotate “clipping preview” overlay (NOT for positioning overlayRef)
    rotateFrameRef.current = {
      left: canvasRect.left - wrapperRect.left,
      top: canvasRect.top - wrapperRect.top,
      width: canvasRect.width,
      height: canvasRect.height,
    };
  };

  useEffect(() => {
    const update = () => updateMetricsAndOverlay();
    update();

    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('scroll', update, true);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', update);
      window.visualViewport.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', update);
        window.visualViewport.removeEventListener('scroll', update);
      }
    };
  }, []);

  // ✅ When tools mutate canvas dimensions or Apply returns rotatePreviewDeg to 0,
  // re-measure BEFORE paint to prevent 1-frame overlay “shift”.
  useLayoutEffect(() => {
    // Only do the expensive DOM measurement when we’re not in rotate-preview.
    // (During slider drag we don’t need to continuously recompute scale.)
    if (rotatePreviewDeg !== 0) return;

    updateMetricsAndOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redrawTrigger, rotatePreviewDeg]);

  useEffect(() => {
    if (!image) return;
    displayImageFromObjectURL(image);
  }, [image]);

  useEffect(() => {
    if (activeTool === 'crop') {
      setCursor('crosshair');
      return;
    }
    if (activeTool === 'text') {
      setCursor('text');
      return;
    }
    if (activeTool === 'stickers' && selectedStickerSrc) {
      setCursor('crosshair');
      return;
    }
    setCursor('auto');
  }, [activeTool, transformMode, selectedStickerSrc]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.style.cursor = cursor;
  }, [cursor, overlayRef]);

  const isRotateMode = activeTool === 'transform' && transformMode === 'rotate';
  const isTextMode = activeTool === 'text' && !isRotateMode;
  const isStickerMode = activeTool === 'stickers' && !isRotateMode;

  const handleOverlayPointerDown = (e) => {
    if (!isTextMode) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    const rect = overlay.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    // Convert to canvas pixels (for export / true placement)
    const sx = scaleRef.current?.x || 1;
    const sy = scaleRef.current?.y || 1;

    const canvasX = cssX * sx;
    const canvasY = cssY * sy;

    setTextInsertPoint({ cssX, cssY, canvasX, canvasY });
  };

  const handleCanvasPointerDown = (e) => {
    console.log('clicked the canvas');
    if (!isStickerMode) return;
    if (!selectedStickerSrc) return;

    // Don’t let wrapper-forwarding also fire
    e.preventDefault();
    e.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const sx = scaleRef.current?.x || 1;
    const sy = scaleRef.current?.y || 1;

    const canvasX = cssX * sx;
    const canvasY = cssY * sy;

    addSticker({ src: selectedStickerSrc, x: canvasX, y: canvasY });
  };

  // ✅ Updated ordering: only preventDefault/stopPropagation in sticker mode
  const handleOverlayPointerDownSticker = (e) => {
    if (!isStickerMode) return;
    if (!selectedStickerSrc) return;

    e.preventDefault();
    e.stopPropagation();

    const overlay = overlayRef.current;
    if (!overlay) return;

    const rect = overlay.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const sx = scaleRef.current?.x || 1;
    const sy = scaleRef.current?.y || 1;

    const canvasX = cssX * sx;
    const canvasY = cssY * sy;

    addSticker({ src: selectedStickerSrc, x: canvasX, y: canvasY });
  };

  // ✅ Catch clicks ANYWHERE in the wrapper (left/right/etc) and forward into the overlay
  const handleWrapperPointerDownCapture = (e) => {
    if (e.target === canvasRef.current) return;
    if (!isTextMode && !isStickerMode) return;

    if (isStickerMode) {
      const clickedPlacedSticker = !!e.target?.closest?.(
        '[data-sticker-item="1"]',
      );

      if (!selectedStickerSrc && !clickedPlacedSticker) {
        setShowStickerPrompt(true);
        return;
      }

      // If they clicked a placed sticker, let that interaction continue normally
      if (clickedPlacedSticker) return;
    }

    const overlay = overlayRef.current;
    if (!overlay) return;

    // If the click is already inside the overlay, let it behave normally
    if (overlay.contains(e.target)) return;

    const rect = overlay.getBoundingClientRect();

    // Forward the click to the nearest point INSIDE the overlay
    const cx = clamp(e.clientX, rect.left + 1, rect.right - 1);
    const cy = clamp(e.clientY, rect.top + 1, rect.bottom - 1);

    e.preventDefault();
    e.stopPropagation();

    const evt = new PointerEvent('pointerdown', {
      bubbles: true,
      cancelable: true,
      clientX: cx,
      clientY: cy,
      pointerId: e.pointerId || 1,
      pointerType: e.pointerType || 'mouse',
    });

    overlay.dispatchEvent(evt);
  };

  return (
    <>
      {showStickerPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[90%] max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <p className="text-center text-sm font-medium text-gray-800">
              Please select a sticker below.
            </p>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setShowStickerPrompt(false)}
                className="rounded-lg bg-black px-4 py-2 text-sm text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        ref={wrapperRef}
        onPointerDownCapture={handleWrapperPointerDownCapture}
        className="relative inline-block h-[360px] sm:h-[450px] md:h-[540px] lg:h-[600px] w-full flex flex-col items-center justify-center p-4 overflow-hidden"
      >
        {/* Keep your current rotate preview behaviour (fit stays as you have it) */}
        <div
          className="relative max-h-full max-w-full w-auto h-auto overflow-hidden bg-white"
          style={{
            zIndex: 20, // ✅ ensure the entire transformed layer is above the mask
            transform: isRotateMode
              ? `translateZ(0) rotate(${rotatePreviewDeg}deg)`
              : 'translateZ(0)',
            transformOrigin: 'center center',
            willChange: isRotateMode ? 'transform' : 'auto',
            backfaceVisibility: 'hidden',
          }}
        >
          <canvas
            ref={canvasRef}
            onPointerDown={handleCanvasPointerDown}
            className={`max-h-full max-w-full w-auto h-auto ${
              activeTool === 'crop' ? 'touch-none select-none' : ''
            }`}
            style={{ cursor, display: 'block', filter: canvasFilter || 'none' }}
          />

          {/* ✅ Overlay stays glued to the canvas container */}
          <div
            ref={overlayRef}
            id="blah"
            className="absolute inset-0"
            onPointerDown={(e) => {
              if (isTextMode) return handleOverlayPointerDown(e);
              if (isStickerMode) return handleOverlayPointerDownSticker(e);
            }}
            style={{
              zIndex: 20, // ✅ ensure overlays always above mask
              pointerEvents: isRotateMode
                ? 'none'
                : activeTool === 'text' || activeTool === 'stickers'
                  ? 'auto'
                  : 'none',
              cursor,
            }}
          >
            <StickersLayer />
          </div>
        </div>

        {/* Rotate “clipping preview” overlay */}
        {isRotateMode && rotatePreviewDeg !== 0 && (
          <div
            id="blocker"
            className="absolute pointer-events-none outline outline-2 outline-dashed outline-stone-400"
            style={{
              zIndex: 40, // ✅ below overlay/text
              left: rotateFrameRef.current.left,
              top: rotateFrameRef.current.top,
              width: rotateFrameRef.current.width,
              height: rotateFrameRef.current.height,
              boxShadow: '0 0 0 9999px rgba(255, 255, 255,0.75)',
              borderRadius: '2px',
            }}
          />
        )}
      </div>
    </>
  );
}
